/**
 * ④PJ の変更一覧の見出しの直下に置く PJ 憲章の中身。
 *
 * **変更を読む前に、案件が何のためかを読めるようにする**（Dhik 2026-09-28「PJ変更の上にPJ憲章を入れて」、
 * 2026-09-29「PJ憲章はタイトルの下に出してほしい」）。レーン列が「何を触ったか」、要求の背景が「なぜ
 * 触ったか」を出すのに対し、憲章は案件そのものの目的・背景・目標を出す。09-29 にいったんタブの奥へ
 * 移したが、同日タブから外して見出しの下へ戻した。節の見出しは置かない — ページの見出しが案件を名指しし、
 * 欄名が中身を言う。
 *
 * **値のある欄を、API が返した順（目的から備考）にそのまま並べる。** 空の欄を落とすのも並びを決めるのも
 * API（読みの正 `readCharter` を通す）で、画面は落とし直さない。
 *
 * **憲章の置き場は 2 つある。** 起票の仕組みより後の案件は Firestore、前の案件は案件仕様書の
 * 「プロジェクト憲章」タブ。どちらから読んだかは出さない — データの出どころは内部の話である（09-28）。
 *
 * **憲章の読取が失敗しても、この節だけを失敗にする。** 憲章は仕様グラフの外にあり、変更の一覧とは
 * 別の読取である。巻き込んで変更の一覧まで消すと、憲章が読めないだけで案件の中身が何も見えなくなる。
 * 案件仕様書を読めなかった（共有されていない 403 を含む）ときは失敗ではなく、節の中の 1 行で出す
 * — 「無い」とは言わない。
 *
 * 無い・案件仕様書を読めなかった・あって全欄が空は、別の 1 行で出し分ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧の見出しの直下に、案件の憲章の値のある欄を欄名と本文で並べ、無い・案件仕様書を読めなかった・全欄が空・失敗・読み込み中を 1 節の中で出し分ける。
 * @graph-connects ErrorState [calls] 憲章の読取の失敗の表示
 */
import type { GetPjCharterOutput } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '../components/spec/fallback-states.js';

/**
 * `PjCharterSection` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ 憲章の節の入力（憲章の読取の答えと失敗）
 * @graph-connects none
 */
export interface PjCharterSectionProps {
  /** 憲章の読取（`getPjCharter`）の答え。まだ来ていなければ undefined */
  data: GetPjCharterOutput | undefined;
  /** 憲章の読取の失敗。失敗していなければ null */
  error: Error | null;
}

/** 「保存されていない」「全欄が空」などの 1 行の見た目。 @graph-connects none */
const NOTE_CLASS = 'text-xs text-[var(--color-text-muted)]';

/**
 * PJ 憲章の節 — 値のある欄の並び（または状態の 1 行）。節の見出しは置かない（名前は `aria-label` だけに持つ）。
 *
 * @param props 憲章の読取の答えと失敗
 * @returns PJ 憲章の節
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の憲章を、値のある欄の欄名と本文で並べる。
 * @graph-connects PjCharterBody [calls] 状態ごとの中身の出し分け
 */
export function PjCharterSection({ data, error }: PjCharterSectionProps) {
  const { t } = useTranslation();

  return (
    <section aria-label={t('pjCharter.title')} data-pj-charter className="flex flex-col gap-2">
      <PjCharterBody data={data} error={error} />
    </section>
  );
}

/**
 * 憲章の中身（失敗 / 読み込み中 / 案件仕様書を読めなかった / 無い / 全欄が空 / 欄の並び）の出し分け。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 憲章の読取を、失敗・読み込み中・案件仕様書を読めなかった・無い・全欄が空・欄の並びの 6 状態で出し分ける。
 * @graph-connects ErrorState [calls] 憲章の読取の失敗の表示
 */
function PjCharterBody({ data, error }: PjCharterSectionProps) {
  const { t } = useTranslation();

  if (error !== null) return <ErrorState error={error} />;
  if (data === undefined) {
    return (
      <div aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="96px" />
      </div>
    );
  }
  if (data.charter === null) {
    // 読めなかったのを「無い」と畳まない。API が古い間は specSheetUnreadable が来ないので無いとして出す
    return data.specSheetUnreadable ? (
      <p data-pj-charter-state="sheet-unreadable" className={NOTE_CLASS}>
        {t('pjCharter.specSheetUnreadable')}
      </p>
    ) : (
      <p data-pj-charter-state="not-saved" className={NOTE_CLASS}>
        {t('pjCharter.notSaved')}
      </p>
    );
  }
  if (data.charter.fields.length === 0) {
    return (
      <p data-pj-charter-state="all-empty" className={NOTE_CLASS}>
        {t('pjCharter.allEmpty')}
      </p>
    );
  }

  return (
    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2.5">
      {data.charter.fields.map((entry) => (
        <div key={entry.field} data-pj-charter-field={entry.field} className="contents">
          <dt className="pt-0.5 text-xs font-bold text-[var(--color-text-secondary)]">
            {t(`pjCharter.fields.${entry.field}`)}
          </dt>
          <dd className="text-sm leading-relaxed break-words whitespace-pre-wrap text-[var(--color-text-primary)]">
            {entry.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
